import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Form, Button, Row, Col, Alert, Spinner, InputGroup,
  Badge, Card, ListGroup
} from 'react-bootstrap';
import {
  Save, X, User, Shirt, Calendar, DollarSign, Ruler,
  Search, AlertCircle, Clock, Package,
  Scissors,
  ImageIcon,
  Upload
} from 'lucide-react';

import customOrderService from '../../../services/customOrderService';
import customerService from '../../../services/customerService';
import modelService from '../../../services/modelService';
import measurementService from '../../../services/measurementService';

/**
 * ✅ ATELIER: Modal de création d'une commande atelier.
 * 
 * Workflow :
 *  1. Choisir une cliente (recherche ou sélection dans la liste)
 *  2. Choisir une fiche de mesures (parmi celles de la cliente)
 *  3. Choisir un modèle (catalogue) OU saisir un nom libre
 *  4. Définir tissu, prix, date de livraison, priorité
 *  5. Enregistrer une avance initiale (optionnel)
 */
const CreateOrderModal = ({ show, onHide, onSuccess, preselectedCustomer = null }) => {

  // ===== ÉTATS =====

  const [formData, setFormData] = useState({
    customerId: '',
    measurementId: '',
    modelId: '',
    useCustomModel: false,
    customModelName: '',
    customModelImageUrl: '',
    fabricDescription: '',
    fabricProvidedByCustomer: true,
    totalPrice: '',
    expectedDeliveryDate: '',
    priority: 'NORMAL',
    notes: '',
    discountType: 'PERCENTAGE', // ou 'FIXED'
    discountValue: '',
    // Avance initiale
    hasInitialAdvance: false,
    initialAdvanceAmount: '',
    initialAdvancePaymentMethod: 'CASH',
    initialAdvanceReference: ''
  });

  // ✅ NOUVEAU: photo du tissu (upload différé après création)
  const [fabricImageFile, setFabricImageFile] = useState(null);
  const [fabricImagePreview, setFabricImagePreview] = useState(null);
  const fabricInputRef = useRef(null);
  const [uploadingFabric, setUploadingFabric] = useState(false);


  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [measurements, setMeasurements] = useState([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);

  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ===== CHARGEMENT INITIAL =====

  useEffect(() => {
    if (show) {
      loadCustomers();
      loadModels();
      resetForm(preselectedCustomer);
    }
  }, [show, preselectedCustomer]);

  // Charger les mesures quand la cliente change
  useEffect(() => {
    if (formData.customerId) {
      loadCustomerMeasurements(formData.customerId);
    } else {
      setMeasurements([]);
    }
  }, [formData.customerId]);

  const resetForm = (customer = null) => {
    const defaultDeliveryDate = new Date();
    defaultDeliveryDate.setDate(defaultDeliveryDate.getDate() + 7);
    const defaultDateStr = defaultDeliveryDate.toISOString().split('T')[0];

    setFormData({
      customerId: customer?.id || '',
      measurementId: '',
      modelId: '',
      useCustomModel: false,
      customModelName: '',
      customModelImageUrl: '',
      fabricDescription: '',
      fabricProvidedByCustomer: true,
      totalPrice: '',
      expectedDeliveryDate: defaultDateStr,
      priority: 'NORMAL',
      notes: '',
      discountType: 'PERCENTAGE',  // ✅ AJOUT
      discountValue: '',           // ✅ AJOUT
      hasInitialAdvance: false,
      initialAdvanceAmount: '',
      initialAdvancePaymentMethod: 'CASH',
      initialAdvanceReference: ''
    });
    setSelectedCustomer(customer);
    setCustomerSearch('');
    setError(null);
  };

  const loadCustomers = async () => {
    try {
      const response = await customerService.getCustomers();
      if (response.success) {
        const list = (response.data || []).map(c =>
          customerService.formatCustomerForDisplay(c)
        );
        setCustomers(list);
      }
    } catch (err) {
      console.error('Erreur chargement clientes:', err);
    }
  };

  const loadModels = async () => {
    try {
      setLoadingModels(true);
      const response = await modelService.getModels();
      if (response.success) {
        setModels(response.data || []);
      }
    } finally {
      setLoadingModels(false);
    }
  };

  const loadCustomerMeasurements = async (customerId) => {
    try {
      setLoadingMeasurements(true);
      const response = await measurementService.getMeasurementsByCustomer(customerId);
      if (response.success) {
        setMeasurements(response.data || []);
        // Auto-sélectionner la fiche la plus récente
        if (response.data && response.data.length > 0) {
          setFormData(prev => ({ ...prev, measurementId: response.data[0].id }));
        }
      }
    } finally {
      setLoadingMeasurements(false);
    }
  };

  // ===== HANDLERS =====

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      handleChange(field, value);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({ ...prev, customerId: customer.id, measurementId: '' }));
    setCustomerSearch('');
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setFormData(prev => ({
      ...prev,
      customerId: '',
      measurementId: ''
    }));
    setMeasurements([]);
  };

  const toggleCustomModel = () => {
    setFormData(prev => ({
      ...prev,
      useCustomModel: !prev.useCustomModel,
      modelId: '',
      customModelName: '',
      customModelImageUrl: ''
    }));
  };

  const handleSelectModel = (modelId) => {
    const model = models.find(m => m.id === modelId);
    setFormData(prev => ({
      ...prev,
      modelId: modelId,
      // Auto-remplir le prix si vide
      totalPrice: prev.totalPrice || (model?.basePrice ? String(model.basePrice) : '')
    }));
  };

  // ✅ NOUVEAU: Gestion de la sélection du fichier de tissu
  const handleFabricImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation locale
    const validation = customOrderService.validateImageFile(file);
    if (!validation.isValid) {
      setError(validation.message);
      return;
    }

    setError(null);
    setFabricImageFile(file);

    // Generate preview
    const reader = new FileReader();
    reader.onload = (ev) => setFabricImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  // ✅ NOUVEAU: Retirer la photo en attente (avant upload)
  const removeFabricImage = () => {
    setFabricImageFile(null);
    setFabricImagePreview(null);
    if (fabricInputRef.current) fabricInputRef.current.value = '';
  };

  // ===== VALIDATION =====

  const validate = () => {
    if (!formData.customerId) {
      return 'Veuillez sélectionner une cliente';
    }

    if (formData.useCustomModel) {
      if (!formData.customModelName.trim()) {
        return 'Veuillez saisir le nom du modèle personnalisé';
      }
    } else {
      if (!formData.modelId) {
        return 'Veuillez choisir un modèle du catalogue (ou cocher "modèle personnalisé")';
      }
    }

    if (!formData.totalPrice || parseFloat(formData.totalPrice) <= 0) {
      return 'Veuillez saisir un prix total valide';
    }

    if (!formData.expectedDeliveryDate) {
      return 'Veuillez choisir une date de livraison prévue';
    }

    // Date de livraison dans le futur
    const deliveryDate = new Date(formData.expectedDeliveryDate);
    deliveryDate.setHours(23, 59, 59);
    if (deliveryDate < new Date()) {
      return 'La date de livraison doit être aujourd\'hui ou dans le futur';
    }

    // Avance initiale
    if (formData.hasInitialAdvance) {
      const advance = parseFloat(formData.initialAdvanceAmount);
      if (!advance || advance <= 0) {
        return 'Le montant de l\'avance est invalide';
      }
      if (advance > parseFloat(formData.totalPrice)) {
        return 'L\'avance ne peut pas dépasser le prix total';
      }
      if (!formData.initialAdvancePaymentMethod) {
        return 'La méthode de paiement pour l\'avance est obligatoire';
      }
    }

    // Validation remise
if (formData.discountValue && parseFloat(formData.discountValue) > 0) {
  const discount = parseFloat(formData.discountValue);
  const total = parseFloat(formData.totalPrice);

  if (formData.discountType === 'PERCENTAGE' && discount > 100) {
    return 'La remise en pourcentage ne peut pas dépasser 100%';
  }

  if (formData.discountType === 'FIXED' && discount >= total) {
    return 'La remise ne peut pas dépasser ou égaler le prix total';
  }
}

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // Construire le payload
      const payload = {
        customerId: formData.customerId,
        measurementId: formData.measurementId || null,
        fabricDescription: formData.fabricDescription,
        fabricProvidedByCustomer: formData.fabricProvidedByCustomer,
        totalPrice: formData.totalPrice,
        expectedDeliveryDate: formData.expectedDeliveryDate,
        priority: formData.priority,
        notes: formData.notes
        
      };

      // Modèle : catalogue OU libre
      if (formData.useCustomModel) {
        payload.customModelName = formData.customModelName;
        payload.customModelImageUrl = formData.customModelImageUrl;
      } else {
        payload.modelId = formData.modelId;
      }

      // Avance initiale
      if (formData.hasInitialAdvance) {
        payload.initialAdvanceAmount = formData.initialAdvanceAmount;
        payload.initialAdvancePaymentMethod = formData.initialAdvancePaymentMethod;
        payload.initialAdvanceReference = formData.initialAdvanceReference;
      }

      // ✅ Remise (optionnelle)
  if (formData.discountValue && parseFloat(formData.discountValue) > 0) {
    payload.discountType = formData.discountType;
    payload.discountValue = formData.discountValue;
    console.log('💰 Remise dans payload:', formData.discountType, formData.discountValue);
  }

      const response = await customOrderService.createOrder(payload);

      if (response.success) {
        console.log('✅ Commande créée:', response.data);
        const createdOrder = response.data;

        // ✅ NOUVEAU: Upload de la photo du tissu si fournie
        if (fabricImageFile && createdOrder?.id) {
          setUploadingFabric(true);
          try {
            const uploadRes = await customOrderService.uploadFabricImage(
              createdOrder.id,
              fabricImageFile
            );
            if (uploadRes.success) {
              console.log('✅ Tissu uploadé:', uploadRes.data);
              // Mettre à jour l'objet commande avec la nouvelle URL pour le callback
              createdOrder.fabricImageUrl = uploadRes.data;
            } else {
              console.warn('⚠️ Commande créée mais échec upload tissu:', uploadRes.message);
              // On ne bloque pas, la commande est créée. L'utilisateur pourra réessayer
              // l'upload depuis OrderDetailModal.
            }
          } catch (uploadErr) {
            console.error('❌ Erreur upload tissu:', uploadErr);
          } finally {
            setUploadingFabric(false);
          }
        }

        onSuccess?.(createdOrder);
        onHide();
      } else {
        setError(response.message || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  // ===== FILTRAGE CLIENTES =====

  const filteredCustomers = customerSearch.trim()
    ? customers.filter(c => {
        const q = customerSearch.trim().toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          (c.phone && c.phone.includes(q))
        );
      })
    : customers;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title className="d-flex align-items-center gap-2">
          <Shirt size={20} />
          Nouvelle commande atelier
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          {/* ========== ÉTAPE 1 : CLIENTE ========== */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
              <User size={18} />
              1. Cliente
            </h6>

            {selectedCustomer ? (
              <Card className="bg-primary bg-opacity-10 border-primary">
                <Card.Body className="py-2 d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{selectedCustomer.name}</strong>
                    {selectedCustomer.phone && (
                      <div className="small text-muted">📞 {selectedCustomer.phone}</div>
                    )}
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleClearCustomer}
                  >
                    <X size={14} /> Changer
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <div>
                <InputGroup className="mb-2">
                  <InputGroup.Text><Search size={14} /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher par nom ou téléphone..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                  />
                </InputGroup>

                <div style={{ maxHeight: '180px', overflowY: 'auto' }} className="border rounded">
                  {filteredCustomers.length === 0 ? (
                    <div className="text-center p-3 text-muted small">
                      Aucune cliente trouvée
                    </div>
                  ) : (
                    <ListGroup variant="flush">
                      {filteredCustomers.slice(0, 20).map(c => (
                        <ListGroup.Item
                          key={c.id}
                          action
                          onClick={() => handleSelectCustomer(c)}
                          className="d-flex justify-content-between align-items-center py-2"
                        >
                          <div>
                            <strong>{c.name}</strong>
                            {c.phone && (
                              <small className="text-muted ms-2">{c.phone}</small>
                            )}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ========== ÉTAPE 2 : MESURES ========== */}
          {formData.customerId && (
            <div className="mb-4">
              <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
                <Ruler size={18} />
                2. Fiche de mesures
                <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.65rem' }}>
                  Optionnel
                </Badge>
              </h6>

              {loadingMeasurements ? (
                <div className="text-center py-2">
                  <Spinner size="sm" /> Chargement...
                </div>
              ) : measurements.length === 0 ? (
                <Alert variant="warning" className="small mb-0">
                  Cette cliente n'a pas de fiche de mesures. Vous pouvez créer la commande sans,
                  mais c'est recommandé d'en créer une.
                </Alert>
              ) : (
                <Form.Select
                  value={formData.measurementId}
                  onChange={(e) => handleChange('measurementId', e.target.value)}
                >
                  <option value="">Aucune fiche</option>
                  {measurements.map(m => (
                    <option key={m.id} value={m.id}>
                      Fiche du {customOrderService.formatDate(m.createdAt)} —{' '}
                      {m.totalMeasurementsCount} mesures
                    </option>
                  ))}
                </Form.Select>
              )}
            </div>
          )}

          {/* ========== ÉTAPE 3 : MODÈLE ========== */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
              <Shirt size={18} />
              3. Modèle
            </h6>

            <Form.Check
              type="switch"
              id="custom-model-switch"
              label="Modèle personnalisé (pas dans le catalogue)"
              checked={formData.useCustomModel}
              onChange={toggleCustomModel}
              className="mb-3"
            />

            {formData.useCustomModel ? (
              <Row className="g-2">
                <Col md={12}>
                  <Form.Control
                    type="text"
                    placeholder="Nom du modèle (ex: Robe inspiration Instagram)"
                    value={formData.customModelName}
                    onChange={(e) => handleChange('customModelName', e.target.value)}
                  />
                </Col>
                <Col md={12}>
                  <Form.Control
                    type="text"
                    placeholder="URL de l'image d'inspiration (optionnel)"
                    value={formData.customModelImageUrl}
                    onChange={(e) => handleChange('customModelImageUrl', e.target.value)}
                  />
                </Col>
              </Row>
            ) : (
              <>
                {loadingModels ? (
                  <Spinner size="sm" />
                ) : models.length === 0 ? (
                  <Alert variant="warning" className="small">
                    Aucun modèle dans le catalogue. Cochez "Modèle personnalisé" pour continuer.
                  </Alert>
                ) : (
                  <Form.Select
                    value={formData.modelId}
                    onChange={(e) => handleSelectModel(e.target.value)}
                  >
                    <option value="">-- Choisir un modèle --</option>
                    {models.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                        {m.category ? ` (${m.category})` : ''}
                        {m.basePrice ? ` — ${modelService.formatPrice(m.basePrice)}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </>
            )}
          </div>

          {/* ========== ÉTAPE 4 : TISSU ========== */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
              <Package size={18} />
              4. Tissu
            </h6>

            <Form.Group className="mb-2">
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Description du tissu (ex: Bazin bleu marine, 6 mètres)"
                value={formData.fabricDescription}
                onChange={(e) => handleChange('fabricDescription', e.target.value)}
              />
            </Form.Group>

            <Form.Check
              type="switch"
              id="fabric-by-customer"
              label="Tissu fourni par la cliente"
              checked={formData.fabricProvidedByCustomer}
              onChange={(e) => handleChange('fabricProvidedByCustomer', e.target.checked)}
            />

            {/* ✅ NOUVEAU: Photo du tissu (optionnelle) */}
            <Form.Group className="mb-3 mt-3">
              <Form.Label className="d-flex align-items-center gap-2">
                <Scissors size={14} className="text-success" />
                Photo du tissu (optionnel)
              </Form.Label>

              <div className="text-center p-3 border rounded bg-light">
                {fabricImagePreview ? (
                  <div className="position-relative d-inline-block">
                    <img
                      src={fabricImagePreview}
                      alt="Aperçu tissu"
                      className="img-thumbnail"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '160px',
                        objectFit: 'cover'
                      }}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={removeFabricImage}
                      style={{ transform: 'translate(50%, -50%)' }}
                      type="button"
                    >
                      <X size={12} />
                    </Button>
                  </div>
                ) : (
                  <div className="py-3">
                    <ImageIcon size={36} className="text-muted mb-2" />
                    <p className="text-muted mb-0 small">Aucune photo</p>
                  </div>
                )}

                <div className="mt-3">
                  <input
                    ref={fabricInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFabricImageChange}
                    style={{ display: 'none' }}
                    id="orderFabricInput"
                  />
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => fabricInputRef.current?.click()}
                    type="button"
                    disabled={loading || uploadingFabric}
                  >
                    <Upload size={14} className="me-1" />
                    {fabricImagePreview ? 'Changer' : 'Sélectionner'}
                  </Button>
                </div>
              </div>

              <Form.Text className="text-muted">
                Photo du tissu apporté · JPG, PNG, WebP · Max 5MB · Optionnel
              </Form.Text>
            </Form.Group>
          </div>

          {/* ========== ÉTAPE 5 : PRIX & LIVRAISON ========== */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
              <DollarSign size={18} />
              5. Prix et livraison
            </h6>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">
                    Prix total <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={formData.totalPrice}
                      onChange={(e) => handleNumberChange('totalPrice', e.target.value)}
                    />
                    <InputGroup.Text>FCFA</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">
                    <Calendar size={12} className="me-1" />
                    Livraison prévue <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.expectedDeliveryDate}
                    onChange={(e) => handleChange('expectedDeliveryDate', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">
                    <Clock size={12} className="me-1" />
                    Priorité
                  </Form.Label>
                  <Form.Select
                    value={formData.priority}
                    onChange={(e) => handleChange('priority', e.target.value)}
                  >
                    <option value="NORMAL">🔵 Normal</option>
                    <option value="URGENT">🔴 Urgent</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>
          {/* ========== ÉTAPE 5.5 : REMISE (OPTIONNELLE) ========== */}
<div className="mb-4">
  <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
    <DollarSign size={18} />
    5.5 Remise (optionnel)
    <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.65rem' }}>
      Optionnel
    </Badge>
  </h6>

  <Row className="g-3">
    <Col md={6}>
      <Form.Group>
        <Form.Label className="small">Type de remise</Form.Label>
        <Form.Select
          value={formData.discountType}
          onChange={(e) => handleChange('discountType', e.target.value)}
        >
          <option value="PERCENTAGE">Pourcentage (%)</option>
          <option value="FIXED">Montant fixe (FCFA)</option>
        </Form.Select>
      </Form.Group>
    </Col>

    <Col md={6}>
      <Form.Group>
        <Form.Label className="small">
          Valeur de la remise
        </Form.Label>
        <InputGroup>
          <Form.Control
            type="text"
            inputMode="decimal"
            placeholder="0"
            value={formData.discountValue}
            onChange={(e) => handleNumberChange('discountValue', e.target.value)}
          />
          <InputGroup.Text>
            {formData.discountType === 'PERCENTAGE' ? '%' : 'FCFA'}
          </InputGroup.Text>
        </InputGroup>
      </Form.Group>
    </Col>

    {/* Aperçu de la remise calculée */}
    {formData.discountValue && parseFloat(formData.discountValue) > 0 
     && formData.totalPrice && parseFloat(formData.totalPrice) > 0 && (
      <Col md={12}>
        <Card className="bg-success bg-opacity-10 border-success">
          <Card.Body className="py-2 px-3">
            {(() => {
              const total = parseFloat(formData.totalPrice);
              const discountVal = parseFloat(formData.discountValue);
              let discountAmount = 0;

              if (formData.discountType === 'PERCENTAGE') {
                discountAmount = (total * discountVal) / 100;
              } else {
                discountAmount = discountVal;
              }

              const finalPrice = total - discountAmount;

              return (
                <Row className="g-2 small">
                  <Col xs={6}>
                    <div className="text-muted">Sous-total :</div>
                    <strong>{total.toLocaleString('fr-FR')} FCFA</strong>
                  </Col>
                  <Col xs={6}>
                    <div className="text-muted">Remise :</div>
                    <strong className="text-danger">
                      -{discountAmount.toLocaleString('fr-FR')} FCFA
                    </strong>
                  </Col>
                  <Col xs={12} className="border-top pt-2 mt-1">
                    <div className="text-muted">Total final :</div>
                    <strong className="text-success" style={{ fontSize: '1.1rem' }}>
                      {finalPrice.toLocaleString('fr-FR')} FCFA
                    </strong>
                  </Col>
                </Row>
              );
            })()}
          </Card.Body>
        </Card>
      </Col>
    )}
  </Row>
</div>

          {/* ========== ÉTAPE 6 : AVANCE INITIALE ========== */}
          <div className="mb-4">
            <Form.Check
              type="switch"
              id="advance-switch"
              label={
                <span className="fw-semibold">
                  <DollarSign size={14} className="me-1" />
                  Enregistrer une avance initiale
                </span>
              }
              checked={formData.hasInitialAdvance}
              onChange={(e) => handleChange('hasInitialAdvance', e.target.checked)}
              className="mb-2"
            />

            {formData.hasInitialAdvance && (
              <Card className="bg-success bg-opacity-10 border-success">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label className="small">
                        Montant avance <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup size="sm">
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          placeholder="0"
                          value={formData.initialAdvanceAmount}
                          onChange={(e) => handleNumberChange('initialAdvanceAmount', e.target.value)}
                        />
                        <InputGroup.Text>FCFA</InputGroup.Text>
                      </InputGroup>
                    </Col>
                    <Col md={6}>
                      <Form.Label className="small">Méthode de paiement <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        size="sm"
                        value={formData.initialAdvancePaymentMethod}
                        onChange={(e) => handleChange('initialAdvancePaymentMethod', e.target.value)}
                      >
                        <option value="CASH">💵 Espèces</option>
                        <option value="WAVE">📱 Wave</option>
                        <option value="ORANGE_MONEY">🟠 Orange Money</option>
                        <option value="FREE_MONEY">🟡 Free Money</option>
                        <option value="BANK_TRANSFER">🏦 Virement</option>
                        <option value="CHECK">📄 Chèque</option>
                        <option value="CARD">💳 Carte</option>
                      </Form.Select>
                    </Col>
                    <Col md={12}>
                      <Form.Label className="small">Référence (optionnel)</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="Ex: WV123456"
                        value={formData.initialAdvanceReference}
                        onChange={(e) => handleChange('initialAdvanceReference', e.target.value)}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* ========== NOTES ========== */}
          <Form.Group className="mb-3">
            <Form.Label className="small">Notes (optionnel)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Observations, préférences de la cliente..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            <X size={16} className="me-1" />
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            
             {loading ? (
              uploadingFabric ? 'Upload du tissu...' : 'Création...'
            ) : (
              <><Save size={16} className="me-1" /> Créer la commande</>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateOrderModal;